import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Clock, X } from "lucide-react";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { supabase } from "@/integrations/supabase/client";
import { formatGBP } from "@/lib/format";
import { Reveal } from "@/components/reveal";
import { SmartImage } from "@/components/smart-image";

type StoredView = {
  id: string;
  title: string;
  image: string | null;
  grading_company: string | null;
  grade: string | null;
  savedAt: number;
};

type Live = StoredView & {
  current_bid_pence: number | null;
  starting_price_pence: number;
  status: string;
};

const KEY = "bidnova:recently-viewed";
const MAX = 12;

function readStore(): StoredView[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX) : [];
  } catch {
    return [];
  }
}

function writeStore(list: StoredView[]) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    /* ignore quota errors */
  }
}

/** Call once on the detail page to register a view. Excludes current from render. */
export function useRegisterRecentlyViewed(item: StoredView | null) {
  useEffect(() => {
    if (!item?.id) return;
    const list = readStore().filter((r) => r.id !== item.id);
    list.unshift({ ...item, savedAt: Date.now() });
    writeStore(list);
  }, [item?.id, item?.title, item?.image, item?.grading_company, item?.grade]);
}

export function RecentlyViewed({ excludeId }: { excludeId?: string }) {
  const [items, setItems] = useState<Live[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = readStore().filter((r) => r.id !== excludeId);
      if (stored.length === 0) {
        setReady(true);
        return;
      }
      const ids = stored.map((s) => s.id);
      const { data } = await supabase
        .from("auctions")
        .select("id,current_bid_pence,starting_price_pence,status")
        .in("id", ids);
      if (cancelled) return;
      const live = new Map(
        (data ?? []).map((r) => [
          r.id,
          {
            current_bid_pence: r.current_bid_pence as number | null,
            starting_price_pence: r.starting_price_pence as number,
            status: r.status as string,
          },
        ]),
      );
      // Drop any listings that no longer exist.
      const kept = stored.filter((s) => live.has(s.id));
      if (kept.length !== stored.length) writeStore(kept);
      setItems(
        kept.map((s) => ({
          ...s,
          ...(live.get(s.id) as Omit<Live, keyof StoredView>),
        })),
      );
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [excludeId]);

  if (!ready || items.length === 0) return null;

  const clear = () => {
    writeStore([]);
    setItems([]);
  };

  return (
    <Reveal className="mt-14">
      <div className="flex items-end justify-between gap-4 mb-4">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary/80" /> Recently viewed
        </h2>
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-3.5 w-3.5" /> Clear
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {items.map((it) => {
          const slug = buildAuctionSlug({
            id: it.id,
            title: it.title,
            grading_company: it.grading_company,
            grade: it.grade,
          });
          const price = it.current_bid_pence ?? it.starting_price_pence;
          return (
            <Link
              key={it.id}
              to="/auction/$slug"
              params={{ slug }}
              className="group block rounded-xl border border-border/60 bg-card/60 overflow-hidden hover-lift"
            >
              <div className="aspect-[3/4] bg-surface-2 overflow-hidden">
                {it.image ? (
                  <SmartImage
                    src={it.image}
                    alt={`${it.title} trading card`}
                    width={200}
                    height={267}
                    widths={[160, 240, 320]}
                    sizes="(max-width: 640px) 40vw, 180px"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : null}
              </div>
              <div className="p-2.5">
                <p className="line-clamp-2 text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                  {it.title}
                </p>
                <p className="mt-1 text-sm font-medium text-gold">{formatGBP(price)}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </Reveal>
  );
}
